import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  EInvoiceDiscountDto,
  EInvoiceItemDto,
  EInvoiceItemType,
  EInvoiceTvaAggregation,
  EInvoiceTvaType,
  EInvoiceExemptTvaZone,
} from "@shared/e-invoice";
import { BaseService } from "@services/base.service";
import { LabelValueModel } from "@shared/models/common/label-value.model";
import { EnumService } from "@services/common";
import _, { Dictionary } from "lodash";
import { EInvoiceComputingService } from "./e-invoice-computing.service";

@Injectable({
  providedIn: "root",
})
export class EInvoiceTvaService extends BaseService {
  constructor(
    private http: HttpClient,
    private computingService: EInvoiceComputingService
  ) {
    super();
  }

  getTvaTypeLabel(status: EInvoiceTvaType): string {
    switch (status) {
      case EInvoiceTvaType.Standard:
        return "Taux de TVA standard";
      case EInvoiceTvaType.Exempt:
        return "Exempté de TVA";
      case EInvoiceTvaType.TvaReverseCharge:
        return "Auto liquidation de TVA";
      case EInvoiceTvaType.IntraCommunityReverseCharge:
        return "Auto liquidation pour cause de livraison intracommunautaire";
      case EInvoiceTvaType.OutsideEuExempt:
        return "Exempté de TVA pour Export hors UE";
      case EInvoiceTvaType.OutOfTvaScope:
        return "Hors du périmètre d'application de la TVA";
      default:
        return "";
    }
  }

  getTvaZoneTypeLabel(status: EInvoiceExemptTvaZone): string {
    switch (status) {
      case EInvoiceExemptTvaZone.France:
        return "France";
      case EInvoiceExemptTvaZone.EuropeenUnion:
        return "Union européenne";
      case EInvoiceExemptTvaZone.OutreMerDepartements:
        return "Départements d’outre-mer";
      case EInvoiceExemptTvaZone.OutreMerTerritories:
        return "Territoire d’outre-Mer";
      
      default:
        return "";
    }
  }

  getTvaTypeOptions(): LabelValueModel[] {
    return EnumService.getNames(EInvoiceTvaType).map((key) => {
      const item = EInvoiceTvaType[key];
      const itemLabel = this.getTvaTypeLabel(item);

      return new LabelValueModel(itemLabel, { key, item });
    });
  }

  getTvaZoneOptions(): LabelValueModel[] {
    return EnumService.getNames(EInvoiceExemptTvaZone).map((key) => {
      const item = EInvoiceExemptTvaZone[key];
      const itemLabel = this.getTvaZoneTypeLabel(item);

      return new LabelValueModel(itemLabel, { key, item });
    });
  }

  computeTvaAggregations(
    invoiceItems: EInvoiceItemDto[],
    discount: EInvoiceDiscountDto
  ): EInvoiceTvaAggregation[] {
    if (discount) {
      let discountInvoiceItem = new EInvoiceItemDto();

      // Get the first reference invoice item in order to capture tva percent.
      let referenceInvoiceItem = _.first(
        invoiceItems.filter((x) => x.Type != EInvoiceItemType.PrepaymentInvoice)
      );

      if (referenceInvoiceItem) {
        discountInvoiceItem.TvaType = referenceInvoiceItem.TvaType;
        discountInvoiceItem.VatPercent = referenceInvoiceItem.VatPercent;
        discountInvoiceItem.Quantity = 1;
        discountInvoiceItem.TotalHt = -discount.AmountHt;
        discountInvoiceItem.TotalTva = -this.computingService.computeTva(
          discount.AmountHt,
          referenceInvoiceItem.VatPercent
        );
        invoiceItems.push(discountInvoiceItem);
      }
    }

    const result = _.chain(invoiceItems)
      .groupBy((x) => {
        return [x.TvaType, x.VatPercent];
      })
      .map((values, key) => {
        let invoiceTvaAggregation = new EInvoiceTvaAggregation();

        if (values[0].TvaType == null) return null;

        invoiceTvaAggregation.Name = this.getTvaTypeLabel(values[0].TvaType);
        invoiceTvaAggregation.VatPercent = values[0].VatPercent;
        invoiceTvaAggregation.TvaType = values[0].TvaType;

        values.forEach((element) => {
          invoiceTvaAggregation.TvaBaseAmount += isNaN(element.TotalHt)
            ? 0
            : element.TotalHt;
        });
        if (invoiceTvaAggregation.VatPercent)
          invoiceTvaAggregation.TvaTotalAmount = _.round(
            (invoiceTvaAggregation.TvaBaseAmount *
              invoiceTvaAggregation.VatPercent) /
              100,
            2
          );

        return invoiceTvaAggregation;
      })
      .filter((x) => x != null)
      .value();

    return result;
  }
}
